/**
 * Instagram AI 投稿アシスタント(spec §10)。
 *
 * 既存 Mail V2 AI 呼び出し(`/api/mail-v2/ai-compose`)を再利用 — 非流式。
 * ユーザー追加指示 / 管理用タイトル / キャプションを渡し、生成された
 * text を regex で `#tag` 抽出して caption + hashtags の候補に反映。
 *
 * Backend / API / DB / Hook signature は一切変更しない。
 * 画像解析は追加しない(spec §10.2 禁止)。
 */

import { useState } from "react";
import { Copy, Loader2, Sparkles } from "lucide-react";

import { mailV2Api } from "../../../api/mail-v2";
import type { SelectedInstagramMedia } from "./InstagramMediaPicker";

// ---------------------------------------------------------------------------
// トーン(spec §10.1) — Mail V2 と整合させる
// "promotion" / "natural" / "polite" / "casual"
// Mail V2 API は "business" | "polite" | "concise" | "custom" だが、
// Mail V2 endpoint は Mail 専用トーン設計のため、custom にフォールバック
// させる方が意図通り(spec §10.2「既存 AI API が受け取る形式に合わせて組み立てる」)。
// Instagram 用トーンは custom で送って、instruction で雰囲気を伝える。
// ---------------------------------------------------------------------------

const TONE_OPTIONS: {
  key: "promotion" | "natural" | "polite" | "casual";
  label: string;
  promptHint: string;
}[] = [
  { key: "promotion", label: "販促", promptHint: "販路を促進する力強いトーン" },
  { key: "natural", label: "自然", promptHint: "自然な柔らかいトーン" },
  { key: "polite", label: "丁寧", promptHint: "丁寧で礼儀正しいトーン" },
  { key: "casual", label: "カジュアル", promptHint: "くだけたカジュアルなトーン" },
];

// ---------------------------------------------------------------------------
// Hashtag regex 抽出(spec §10.3 自動上書きしない方針 — ユーザー操作で反映)
// 候補生成時のみ使う。保存時に buildFinalCaption で caption 末尾へ連結。
// ---------------------------------------------------------------------------

function extractHashtags(text: string): string[] {
  const matches = text.match(/#[\wぁ-んァ-ヶ一-龯]+/g) ?? [];
  return matches.map((m) => m.replace(/^#+/, ""));
}

function stripHashtags(text: string): string {
  return text.replace(/[#][\wぁ-んァ-ヶ一-龯]+\s*/g, "").trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InstagramAiAssistant({
  managementTitle,
  caption,
  selectedMedia,
  onApplyCaption,
  onApplyHashtags,
}: {
  managementTitle: string;
  caption: string;
  selectedMedia: SelectedInstagramMedia | undefined;
  onApplyCaption: (text: string) => void;
  onApplyHashtags: (tags: string[]) => void;
}) {
  const [tone, setTone] = useState<typeof TONE_OPTIONS[number]["key"]>(
    "natural",
  );
  const [instruction, setInstruction] = useState("");
  const [generated, setGenerated] = useState<{
    text: string;
    caption: string;
    hashtags: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      // spec §10.2: 管理用タイトル / キャプション / トーン / ユーザー指示 / ファイル名 を渡す
      const toneHint = TONE_OPTIONS.find((t) => t.key === tone)?.promptHint ?? "";
      const parts = [
        toneHint ? `トーン: ${toneHint}` : null,
        managementTitle ? `タイトル: ${managementTitle}` : null,
        caption ? `既存キャプション: ${caption}` : null,
        selectedMedia ? `画像: ${selectedMedia.fileName}` : null,
        instruction ? `追加指示: ${instruction}` : null,
      ].filter(Boolean);
      const composedInstruction =
        parts.length > 0
          ? parts.join("\n")
          : "Instagram 投稿用のキャプションとハッシュタグを生成してください。";

      const result = await mailV2Api.generateAiCompose({
        instruction: composedInstruction,
        tone: "custom",
      });

      const text = result.text ?? "";
      const tags = extractHashtags(text);
      const plain = stripHashtags(text);

      setGenerated({
        text,
        caption: plain,
        hashtags: tags,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "AI 生成に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.text);
    } catch {
      /* clipboard 失敗は silent */
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <Sparkles size={14} color="#7c3aed" />
        <strong style={{ fontSize: 13, color: "var(--ig-text)" }}>
          AI投稿アシスタント
        </strong>
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--ig-muted)",
          marginBottom: 12,
          lineHeight: 1.5,
        }}
      >
        キャプションやハッシュタグの候補を生成します
      </div>

      <label className="ig-field-label">投稿トーン</label>
      <div className="ig-ai-tone-group">
        {TONE_OPTIONS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`ig-ai-tone-pill ${
              tone === t.key ? "is-active" : ""
            }`}
            onClick={() => setTone(t.key)}
            disabled={loading}
          >
            {t.label}
          </button>
        ))}
      </div>

      <label className="ig-field-label">AIへの指示（任意）</label>
      <textarea
        className="ig-ai-instruction"
        placeholder="例: 新商品のアピールポイントを3つ含めて"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        disabled={loading}
      />

      <button
        type="button"
        className="ig-gradient-button ig-gradient-button--ai"
        onClick={handleGenerate}
        disabled={loading}
        style={{ width: "100%" }}
      >
        {loading ? (
          <Loader2 size={14} className="ig-spin" />
        ) : (
          <Sparkles size={14} />
        )}
        キャプション・ハッシュタグを生成
      </button>

      {error ? <div className="ig-ai-error">{error}</div> : null}

      {generated ? (
        <div className="ig-ai-result">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <strong style={{ fontSize: 12, color: "var(--ig-text)" }}>
              生成結果
            </strong>
            <button
              type="button"
              className="ig-button"
              onClick={handleCopy}
              style={{
                height: 26,
                fontSize: 11,
                padding: "0 10px",
              }}
            >
              <Copy size={11} />
              コピー
            </button>
          </div>

          <div className="ig-ai-result-caption">{generated.caption}</div>

          {generated.hashtags.length > 0 ? (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              {generated.hashtags.map((t) => (
                <span key={t} className="ig-hashtag-chip">
                  #{t}
                </span>
              ))}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="ig-button"
              style={{ height: 30, fontSize: 11 }}
              onClick={() => onApplyCaption(generated.caption)}
            >
              本文へ反映
            </button>
            {generated.hashtags.length > 0 ? (
              <button
                type="button"
                className="ig-button"
                style={{ height: 30, fontSize: 11 }}
                onClick={() => onApplyHashtags(generated.hashtags)}
              >
                ハッシュタグを反映
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}